/**
 * Composer-Favoriten (Phase 2): Modus-/Werkzeug-IDs in localStorage anheften.
 * Nur Feature-IDs — keine Namen, keine Mandanten-PII.
 */

export type ComposerModePinId = 'mode:fragen' | 'mode:verfassen' | 'mode:zusammenfassen';
/** Analysieren bewusst ausgelassen — Slot = ArkoComposerSearchChip. */
export type ComposerToolPinId =
  | 'tool:umformulieren'
  | 'tool:erklaeren'
  | 'tool:zusammenfassen'
  | 'tool:speichern'
  | 'tool:rechtsfrage';

export type ComposerPinId = ComposerModePinId | ComposerToolPinId;

export type ComposerPinKind = 'mode' | 'tool';

export interface ComposerPinMeta {
  id: ComposerPinId;
  kind: ComposerPinKind;
  label: string;
  /** Hub-Mode-ID (nur kind=mode). */
  modeId?: 'fragen' | 'verfassen' | 'zusammenfassen';
  /** Quick-Action-Intent (nur kind=tool). */
  intent?: 'umformulieren' | 'erklaeren' | 'zusammenfassen' | 'speichern' | 'rechtsfrage';
  promptPrefix?: string;
  /** PortalChipBar-Tab für Pin-Chrome. */
  toolsTab?: 'schnell' | 'recht';
}

const STORAGE_PREFIX = 'arko-composer-pins';

/** Default: leere Leiste — Modus-/Werkzeuge-Dropdowns bleiben der Einstieg. */
export const DEFAULT_COMPOSER_PINS: ComposerPinId[] = [];

export const COMPOSER_MODE_PIN_CATALOG: ComposerPinMeta[] = [
  { id: 'mode:fragen', kind: 'mode', label: 'Fragen', modeId: 'fragen' },
  { id: 'mode:verfassen', kind: 'mode', label: 'Verfassen', modeId: 'verfassen' },
  { id: 'mode:zusammenfassen', kind: 'mode', label: 'Zusammenfassen', modeId: 'zusammenfassen' },
];

export const COMPOSER_TOOL_PIN_CATALOG: ComposerPinMeta[] = [
  {
    id: 'tool:umformulieren',
    kind: 'tool',
    label: 'Umformulieren',
    intent: 'umformulieren',
    promptPrefix: 'Bitte formuliere folgenden Text professionell um:\n\n',
    toolsTab: 'schnell',
  },
  {
    id: 'tool:erklaeren',
    kind: 'tool',
    label: 'Erklären',
    intent: 'erklaeren',
    promptPrefix: 'Erkläre mir bitte folgendes verständlich:\n\n',
    toolsTab: 'schnell',
  },
  {
    id: 'tool:zusammenfassen',
    kind: 'tool',
    label: 'Zusammenfassen',
    intent: 'zusammenfassen',
    promptPrefix: 'Fasse folgenden Text präzise zusammen:\n\n',
    toolsTab: 'schnell',
  },
  {
    id: 'tool:speichern',
    kind: 'tool',
    label: 'Speichern',
    intent: 'speichern',
    promptPrefix: 'Bitte speichere folgende Information in deinem Gedächtnis:\n\n',
    toolsTab: 'schnell',
  },
  {
    id: 'tool:rechtsfrage',
    kind: 'tool',
    label: 'Rechtsfragen',
    intent: 'rechtsfrage',
    promptPrefix: 'Rechtsfrage (verifizierte Normen aus LEXCORE):\n\n',
    toolsTab: 'recht',
  },
];

export const COMPOSER_PIN_CATALOG: ComposerPinMeta[] = [
  ...COMPOSER_MODE_PIN_CATALOG,
  ...COMPOSER_TOOL_PIN_CATALOG,
];

const PIN_SET = new Set<string>(COMPOSER_PIN_CATALOG.map((p) => p.id));

export function isComposerPinId(value: unknown): value is ComposerPinId {
  return typeof value === 'string' && PIN_SET.has(value);
}

export function getComposerPinMeta(id: ComposerPinId): ComposerPinMeta | undefined {
  return COMPOSER_PIN_CATALOG.find((p) => p.id === id);
}

export function modeIdToPinId(
  modeId: string,
): ComposerModePinId | null {
  if (modeId === 'fragen' || modeId === 'verfassen' || modeId === 'zusammenfassen') {
    return `mode:${modeId}`;
  }
  return null;
}

/** Storage-Key: user-id wenn Session, sonst lokal. */
export function composerPinsStorageKey(userId?: string | null): string {
  const uid = typeof userId === 'string' ? userId.trim() : '';
  return uid ? `${STORAGE_PREFIX}:u:${uid}` : `${STORAGE_PREFIX}:local`;
}

function sanitizePins(raw: unknown): ComposerPinId[] {
  if (!Array.isArray(raw)) return [...DEFAULT_COMPOSER_PINS];
  const seen = new Set<string>();
  const out: ComposerPinId[] = [];
  for (const item of raw) {
    if (!isComposerPinId(item) || seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

export function loadComposerPins(userId?: string | null): ComposerPinId[] {
  if (typeof localStorage === 'undefined') return [...DEFAULT_COMPOSER_PINS];
  try {
    const raw = localStorage.getItem(composerPinsStorageKey(userId));
    if (!raw) return [...DEFAULT_COMPOSER_PINS];
    return sanitizePins(JSON.parse(raw) as unknown);
  } catch {
    return [...DEFAULT_COMPOSER_PINS];
  }
}

export function saveComposerPins(pins: ComposerPinId[], userId?: string | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(composerPinsStorageKey(userId), JSON.stringify(sanitizePins(pins)));
  } catch {
    /* quota / private mode */
  }
}

export function toggleComposerPin(
  pins: ComposerPinId[],
  id: ComposerPinId,
): ComposerPinId[] {
  if (pins.includes(id)) return pins.filter((p) => p !== id);
  return [...pins, id];
}

export function isPinActive(pins: readonly ComposerPinId[], id: ComposerPinId): boolean {
  return pins.includes(id);
}
